import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Typography, IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Delete } from "@mui/icons-material";
import ButtonCst from "@components/ButtonCst";
import SelectCst from "@components/InputCst/SelectCst";
import { EuroTextCst } from "@components/InputCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { defaultValidation } from "@utils/utilsValidators";
import { ColumnInterface } from "@components/Table/types";
import { Association } from "src/interfaces/Common";
import { useNavigate } from "react-router-dom";

// --- Props Interface ---
interface AccountingProjectFormCstInterface {
  row?: any;
  columns: ColumnInterface[];
  additionalColumns?: ColumnInterface[];
  onSubmit: (inputForm: any, sessions: any) => void;
  onReset?: () => void;
  formActionType?: string[];
  inputsException?: (col: any, inputForm: any) => any;
  backButton?: () => void;
  getValues?: (accessorKey: string) => any;
  handleDelete?: (accessorKey: string, element: any) => void;
  handleAssociation?: (accessorKey: string, value: any) => void;
  setOpenAssociation?: (
    accessorKey: string,
    open: boolean,
    inputForm: any,
  ) => void;
  resetCostProjectAssociations?: () => void;
  association?: boolean | Association;
  isFaculties?: boolean;
  disabledConfig?: { view: string; accessoryKey: string };
  readOnly?: boolean;
  isCorporate?: boolean;
  deleteDisabled?: boolean;
  options?: any;
  optionsSelect?: any;
  getOptionValue?: any;
  durationKey?: boolean;
  fullWidth?: boolean;
  dataCost?: any;
}

// --- Helpers ---
const createEmptyPair = (idx: number) => ({
  budget: {
    accessorKey: `budget_${idx}`,
    type: "select",
    required: true,
    header: "Budget",
    labelHeader: { en: "Budget", it: "Budget" },
  },
  value: {
    accessorKey: `value_${idx}`,
    type: "euro-text",
    required: true,
    header: "Value",
    labelHeader: { en: "Value", it: "Valore" },
    rules: {
      maxValue: { value: 100000000, msg: "invalid-number" },
      minValue: { value: 0, msg: "invalid-number" },
    },
  },
});

// --- Component ---
const AccountingProjectFormCst: React.FC<AccountingProjectFormCstInterface> = ({
  row,
  columns,
  inputsException,
  onSubmit,
  onReset,
  backButton,
  getValues,
  handleDelete,
  handleAssociation,
  setOpenAssociation,
  resetCostProjectAssociations,
  association,
  readOnly,
  optionsSelect,
  fullWidth = false,
  dataCost,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["form"]);
  const navigate = useNavigate();

  // --- State ---
  const [inputForm, setInputForm] = useState<Record<string, any>>({});
  const [errorInputForm, setErrorInputForm] = useState<Record<string, any>>({});
  const [localColumns, setLocalColumns] = useState<
    { budget: any; value: any }[]
  >([]);
  const [columnPairCounter, setColumnPairCounter] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [noSave, setNoSave] = useState(false);
  const [extraOptions, setExtraOptions] = useState<any[]>([]);
  const [editDetailObject, setEditDetailObject] = useState<any>({});
  const [newSession, setNewSession] = useState<boolean>(false);
  const [details, setDetails] = useState<any[]>([]);

  // --- Effects ---
  // Popola colonne e inputForm da row o crea 2 coppie vuote
  useEffect(() => {
    if (Array.isArray(row) && row.length > 0) {
      const initialState: Record<string, any> = {};
      const generatedColumns = row.map((item: any, idx: number) => {
        const budgetKey = `budget_${idx + 1}`;
        const valueKey = `value_${idx + 1}`;
        initialState[budgetKey] = item.budget_id ?? "";
        initialState[valueKey] = item.value ?? "";
        return createEmptyPair(idx + 1);
      });
      setLocalColumns(generatedColumns);
      setInputForm(initialState);
      setColumnPairCounter(row.length + 1);
    } else if (localColumns.length === 0) {
      setLocalColumns([createEmptyPair(1), createEmptyPair(2)]);
      setInputForm({
        budget_1: "",
        value_1: "",
        budget_2: "",
        value_2: "",
      });
      setColumnPairCounter(3);
    }
  }, [row]);

  // Aggiorna extraOptions da dataCost
  useEffect(() => {
    const { compatible_budgets } = dataCost || {};
    if (compatible_budgets?.length > 0) {
      setExtraOptions(
        compatible_budgets.map((b: any) => ({
          value: `${b.id}`,
          label: b.year,
        })),
      );
    } else {
      setExtraOptions([]);
    }
  }, [dataCost]);

  // Mostra/nascondi bottone aggiunta in base al totale
  useEffect(() => {
    const total = calculateTotalAllocatedValue(inputForm);
    setShowAdd(total < dataCost?.total_cost);
    setNoSave(total > dataCost?.total_cost);
  }, [inputForm, dataCost]);

  // --- Utility ---
  const calculateTotalAllocatedValue = (form: Record<string, any>): number =>
    Object.keys(form)
      .filter((k) => k.startsWith("value_"))
      .reduce((sum, k) => sum + (Number(form[k]) || 0), 0);

  // --- Validation & Submit ---
  const onConfirm = () => {
    let canBeSubmit = true;
    let showError: Record<string, any> = {};

    localColumns.forEach((pair) => {
      // Validazione budget
      if (pair.budget) {
        const { canBeSubmit: ok, showError: err } = defaultValidation(
          [pair.budget],
          inputForm,
        );
        if (!ok) canBeSubmit = false;
        showError = { ...showError, ...err };
      }
      // Validazione value
      if (pair.value) {
        const { canBeSubmit: ok, showError: err } = defaultValidation(
          [pair.value],
          inputForm,
        );
        if (!ok) canBeSubmit = false;
        showError = { ...showError, ...err };
      }
    });

    // Duplicati budget
    const seenBudgets = new Set();
    localColumns.forEach((pair) => {
      const budgetKey = pair.budget?.accessorKey;
      const budgetValue = Number(inputForm[budgetKey]);
      if (budgetValue) {
        if (seenBudgets.has(budgetValue)) {
          canBeSubmit = false;
          showError[budgetKey] = "invalid-budget-in-use";
        } else {
          seenBudgets.add(budgetValue);
        }
      }
    });

    setErrorInputForm(showError);

    if (canBeSubmit) {
      onSubmit(inputForm, details);
    }
  };

  // --- Gestione colonne dinamiche ---
  const handleAddColumnPair = () => {
    const idx = columnPairCounter;
    setLocalColumns((prev) => [...prev, createEmptyPair(idx)]);
    setInputForm((prev) => ({
      ...prev,
      [`budget_${idx}`]: "",
      [`value_${idx}`]: "",
    }));
    setColumnPairCounter(idx + 1);
  };

  const handleRemovePair = (idx: number) => {
    setLocalColumns((prev) => prev.filter((_, i) => i !== idx));
    setInputForm((prev) => {
      const updated = { ...prev };
      delete updated[`budget_${idx + 1}`];
      delete updated[`value_${idx + 1}`];
      return updated;
    });
  };

  // --- Render input dinamici ---
  const getInputsList = (col: any) => {
    if (inputsException) col = inputsException(col, inputForm);
    if (col.visibilityInForm === false) return null;

    if (col.type === "euro-text") {
      return (
        <Grid key={col.accessorKey} item xs={12} sm={6}>
          <EuroTextCst
            key={col.accessorKey}
            id={col.accessorKey}
            label={col.header}
            value={inputForm[col.accessorKey]}
            required={col.required}
            onChange={(e: any) =>
              setInputForm({ ...inputForm, [col.accessorKey]: e.target.value })
            }
            error={!!errorInputForm[col.accessorKey]}
            errorMessage={
              t(
                `error.field.${errorInputForm[col.accessorKey]?.msg || errorInputForm[col.accessorKey]}`,
              ) || "Error"
            }
            disabled={col.disabled}
            type={col.inpuType || "text"}
          />
        </Grid>
      );
    }
    if (col.type === "select") {
      return (
        <Grid key={col.accessorKey} item xs={12} sm={5}>
          <SelectCst
            key={col.accessorKey}
            id={col.accessorKey}
            label={col.header}
            value={inputForm[col.accessorKey]}
            required={col.required}
            onChange={(e: any) =>
              setInputForm({ ...inputForm, [col.accessorKey]: e })
            }
            options={col.options}
            extraOptions={extraOptions}
            error={!!errorInputForm[col.accessorKey]}
            errorMessage={
              t(
                `error.field.${errorInputForm[col.accessorKey]?.msg || errorInputForm[col.accessorKey]}`,
              ) || "Error"
            }
            disabled={col.disabled}
          />
        </Grid>
      );
    }
    return null;
  };

  // --- Render ---
  return (
    <>
      {!association && (
        <Grid container>
          <Grid item xs={0} lg={fullWidth ? 0 : 4} xl={fullWidth ? 0 : 3}>
            <BackgroundImageCst
              image="section-internal-form.jpg"
              disableGradient
            />
          </Grid>
          <Grid item xs={12} lg={fullWidth ? 12 : 8} xl={fullWidth ? 12 : 9}>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                padding: "22px",
              }}
            >
              {/* Totale */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ mr: 1 }}>
                  {t("accounting-form.total-cost")} -
                </Typography>
                <Typography variant="body1">
                  {new Intl.NumberFormat("it-IT", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(Number(dataCost?.total_cost || 0))}
                  €
                </Typography>
              </Box>
              <div
                style={{ borderBottom: "1px dashed #6d6d6d", marginBottom: 20 }}
              />

              {/* Coppie budget/value */}
              {localColumns.map((pair, idx) => (
                <Grid
                  container
                  key={pair.budget.accessorKey + pair.value.accessorKey}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  {getInputsList(pair.budget)}
                  {getInputsList(pair.value)}
                  <Grid
                    item
                    xs={12}
                    sm={1}
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButton
                      color="primary"
                      aria-label="delete"
                      onClick={() => handleRemovePair(idx)}
                    >
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {/* Bottone aggiungi */}
              {showAdd && (
                <Box>
                  <ButtonCst
                    id="add-button"
                    variant="contained"
                    size="medium"
                    sx={{ minWidth: "150px" }}
                    onClick={handleAddColumnPair}
                    disabled={
                      newSession || !!Object.keys(editDetailObject).length
                    }
                  >
                    {t("accounting-form.add-accounting")}
                  </ButtonCst>
                </Box>
              )}

              <div
                style={{ borderBottom: "1px dashed #6d6d6d", marginTop: 20 }}
              />

              {/* Bottoni azione */}
              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    mt: "32px",
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    justifyContent: "space-between",
                  }}
                >
                  <ButtonCst
                    id="back-button"
                    variant="outlined"
                    size="medium"
                    sx={{ minWidth: "150px", mr: "auto" }}
                    onClick={() => (backButton ? backButton() : navigate(-1))}
                  >
                    {t("backButton")}
                  </ButtonCst>
                  <ButtonCst
                    id="save-button"
                    variant="contained"
                    size="medium"
                    sx={{ minWidth: "150px" }}
                    onClick={onConfirm}
                    disabled={
                      !!readOnly || noSave || !(localColumns.length > 0)
                    }
                  >
                    {t("saveButton")}
                  </ButtonCst>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AccountingProjectFormCst;
